import { atom, useAtomValue, useSetAtom, useStore } from "jotai";
import { useCallback, useMemo } from "react";
import { streamsAtom, type StreamingState } from "./streamStore";

const matchesSession = (stream: StreamingState, sessionId: string | undefined) =>
  sessionId ? stream.sessionId === sessionId : stream.sessionId == null;

const isStreamingIn = (
  streams: Map<string, StreamingState>,
  sessionId: string | undefined,
) => {
  for (const stream of streams.values()) {
    if (stream.isStreaming && matchesSession(stream, sessionId)) return true;
  }
  return false;
};

/**
 * Whether a stream of the session runs. useStreams renders its users on each chunk
 * of a stream; this renders only when the answer changes.
 */
export const useIsSessionStreaming = (sessionId: string | undefined) =>
  useAtomValue(
    useMemo(() => atom((get) => isStreamingIn(get(streamsAtom), sessionId)), [sessionId]),
  );

const streamingSessionsAtom = atom((get) =>
  Array.from(get(streamsAtom).values())
    .flatMap((stream) => (stream.isStreaming && stream.sessionId ? [stream.sessionId] : []))
    .sort()
    .join("\n"),
);

/** The sessions with a running stream; renders only when this set changes. */
export const useStreamingSessionIds = () => {
  const key = useAtomValue(streamingSessionsAtom);
  return useMemo(() => new Set(key ? key.split("\n") : []), [key]);
};

/** The streams with those of the session stopped; the same map when none of them runs. */
export const stopSessionStreams = (
  streams: Map<string, StreamingState>,
  sessionId: string | undefined,
) => {
  let changed = false;
  const next = new Map(streams);
  for (const [id, stream] of next) {
    if (stream.isStreaming && matchesSession(stream, sessionId)) {
      stream.abortController.abort();
      next.set(id, { ...stream, isStreaming: false, aborted: true, fadeHold: true });
      changed = true;
    }
  }
  return changed ? next : streams;
};

/** Stops the streams of a session, without rendering on chunks as useStreams does. */
export const useAbortSessionStreams = () => {
  const setStreams = useSetAtom(streamsAtom);
  return useCallback(
    (sessionId: string | undefined) => setStreams((prev) => stopSessionStreams(prev, sessionId)),
    [setStreams],
  );
};

/** Reads whether a session streams at the time of the call, without rendering on chunks. */
export const useReadSessionStreaming = () => {
  const store = useStore();
  return useCallback(
    (sessionId: string) => isStreamingIn(store.get(streamsAtom), sessionId),
    [store],
  );
};
